import React, { useMemo } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import { groupRowsIntoSections } from './viewModeUtils';

import { formatFigure } from 'Helpers/formatService';

import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import StarBadgeIcon from 'Assets/icons/star-badge.svg';

const BLANK_PLACEHOLDER = '—';
const COLUMN_COUNT = 7;

const formatDateCell = (value) => (value ? moment(value).format('DD-MM-YYYY') : BLANK_PLACEHOLDER);

// A row is "at risk" when its at-risk amount is a positive money value — the
// backend only sets it for a slipping/overdue row that also has exposure.
const isAtRisk = (row) => Number(row.exposure_at_risk) > 0;

/**
 * Sum the exposure / at-risk amounts across a set of activity rows. Null
 * exposures (no tagged position) contribute nothing to the roll-up.
 */
const sumExposure = (rows) =>
    rows.reduce(
        (acc, row) => ({
            exposure: acc.exposure + (Number.isFinite(row.exposure) ? row.exposure : 0),
            atRisk: acc.atRisk + (Number.isFinite(row.exposure_at_risk) ? row.exposure_at_risk : 0),
        }),
        { exposure: 0, atRisk: 0 },
    );

const HEADERS = [
    { key: 'milestone', label: () => $t('Milestone'), align: 'start' },
    { key: 'category', label: () => $t('Category'), align: 'start' },
    { key: 'target', label: () => $t('Target'), align: 'start' },
    { key: 'estimated', label: () => $t('Estimated'), align: 'start' },
    { key: 'variance', label: () => $t('Variance'), align: 'end' },
    { key: 'exposure', label: () => $t('Exposure'), align: 'end' },
    { key: 'at_risk', label: () => $t('At-risk'), align: 'end' },
];

const EmptyRow = () => (
    <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
        <td colSpan={COLUMN_COUNT} className="tw-p-4">
            <span>{$t('No data available')}</span>
        </td>
    </tr>
);

const ActivityRow = ({ row }) => {
    const atRisk = isAtRisk(row);
    // The API sends BLANK_PLACEHOLDER as the variance when there is no estimated
    // date: no figure, so the cell is blank and never red.
    const variance = row.variance === BLANK_PLACEHOLDER ? null : row.variance;

    return (
        <tr
            className={classNames('tw-text-xs !tw-bg-white', {
                '!tw-bg-red/5': atRisk,
            })}
        >
            <td className="!tw-bg-charcoal-15 tw-text-charcoal">
                <div className="tw-flex tw-items-center tw-gap-x-1.5">
                    {row.is_key_milestone && <StarBadgeIcon className="tw-h-3 tw-w-3 tw-text-blue tw-shrink-0" title={$t('Key milestone')} />}
                    <span>{row.description}</span>
                </div>
            </td>
            <td>{row.category || BLANK_PLACEHOLDER}</td>
            <td className="inbud-date-cell">{formatDateCell(row.target_completion_date)}</td>
            <td className={classNames('inbud-date-cell', { '!tw-text-red': row.is_exception })}>{formatDateCell(row.estimated_completion_date)}</td>
            <td className={classNames('inbud-numeric-cell', { '!tw-text-red': row.is_exception && variance !== null && Number(variance) !== 0 })}>
                {formatFigure(variance, String)}
            </td>
            <td className="inbud-numeric-cell tw-font-medium">{formatFigure(row.exposure)}</td>
            <td className={classNames('inbud-numeric-cell tw-font-medium', { '!tw-text-red': atRisk })}>{formatFigure(row.exposure_at_risk)}</td>
        </tr>
    );
};

const SubtotalRow = ({ label, totals }) => (
    <tr className="tw-text-xs tw-font-semibold !tw-bg-blue/5">
        <td className="!tw-bg-blue/10 tw-text-charcoal" colSpan={5}>
            {label}
        </td>
        <td className="inbud-numeric-cell">{formatFigure(totals.exposure)}</td>
        <td className={classNames('inbud-numeric-cell', { '!tw-text-red': totals.atRisk > 0 })}>{formatFigure(totals.atRisk)}</td>
    </tr>
);

/**
 * Milestone-Budget Exposure view (IB-674).
 *
 * A grouped table over the exposure-mode detailed rows: each activity's tagged-
 * position labour cost (`exposure`) and, when slipping/overdue, the same amount
 * flagged as `exposure_at_risk`. Per-group subtotals and a grand total roll the
 * two amounts up; at-risk rows are tinted red (parity with the report's overdue
 * row styling).
 */
const ExposureTable = ({ rows, loading }) => {
    const sections = useMemo(() => groupRowsIntoSections(rows), [rows]);
    const activityRows = useMemo(() => (rows || []).filter((row) => !row._is_group), [rows]);
    const grandTotals = useMemo(() => sumExposure(activityRows), [activityRows]);

    const hasRows = activityRows.length > 0;

    return (
        <table className="inbud-table inbud-table_first-sticky">
            <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2">
                    <th className="tw-z-10 tw-w-1/3 tw-min-w-72">{HEADERS[0].label()}</th>
                    {HEADERS.slice(1).map((header) => (
                        <th key={header.key} className={classNames('tw-min-w-32', { 'tw-text-end': header.align === 'end' })}>
                            {header.label()}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {loading && <TableRowSkeleton rows={10} columns={COLUMN_COUNT} />}
                {!loading && !hasRows && <EmptyRow />}
                {!loading &&
                    hasRows &&
                    sections.map((section, sectionIndex) => {
                        const totals = sumExposure(section.rows);

                        return (
                            <React.Fragment key={`section-${sectionIndex}`}>
                                {section.label !== '' && (
                                    <tr className="tw-font-medium !tw-bg-charcoal-75">
                                        <td className="tw-text-white" colSpan={COLUMN_COUNT}>
                                            {section.label}
                                        </td>
                                    </tr>
                                )}
                                {section.rows.map((row, rowIndex) => (
                                    <ActivityRow key={row.id ?? `${sectionIndex}-${rowIndex}`} row={row} />
                                ))}
                                <SubtotalRow label={$t('Subtotal')} totals={totals} />
                            </React.Fragment>
                        );
                    })}
                {!loading && hasRows && (
                    <tr className="tw-text-xs tw-font-semibold !tw-bg-charcoal-75 tw-text-white">
                        <td className="!tw-bg-charcoal-75 tw-text-white" colSpan={5}>
                            {$t('Total')}
                        </td>
                        <td className="inbud-numeric-cell">{formatFigure(grandTotals.exposure)}</td>
                        <td className={classNames('inbud-numeric-cell', { '!tw-text-red': grandTotals.atRisk > 0 })}>{formatFigure(grandTotals.atRisk)}</td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};

export default ExposureTable;
